import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMAWatchlistStore } from "../../Store/maWatchlistStore";
import { useMAWatchlistFilterStore } from "../../Store/maWatchlistFilterStore";
import {
  buildWatchlistCriteriaState,
  getWatchlistCriteriaComparisonKey,
  parseWatchlistCharacteristics,
} from "../../Utils/maWatchlistFilterUtils";
import {
  normalizeCusip,
  normalizeCusipList,
} from "../../Utils/maInventoryMarketDataUtils";
import {
  MA_DYNAMIC_WATCHLIST_CUSIP_LIMIT,
  MA_DYNAMIC_WATCHLIST_LIMIT_MESSAGE,
  MA_STATIC_WATCHLIST_CUSIP_LIMIT,
  MA_STATIC_WATCHLIST_LIMIT_MESSAGE,
} from "../../Constants/AppConstants";
import {
  canRetryFailedDynamicWatchlist,
  getWatchlistDisplayName,
  getWatchlistRetryMessage,
  getWatchlistProcessingMessage,
  getWatchlistStatusUpdatePayload,
  isBackendDynamicWatchlist,
  isWatchlistFailed,
  isWatchlistProcessing,
  normalizeWatchlistStatus,
  WATCHLIST_STATUS,
} from "../../Utils/maWatchlistStatusUtils";
import {
  WATCHLIST_DEFAULT_ROWS_PER_PAGE,
  WATCHLIST_TABLE_VIEWPORT_HEIGHT,
} from "./MAWatchlistTableSection";

export const WATCHLIST_TABS = {
  ALL: 0,
  MY_WATCHLIST: 1,
};

export const WATCHLIST_TYPE = {
  STATIC: "static",
  DYNAMIC: "dynamic",
};

export const CREATE_WATCHLIST_ID = "__create_watchlist__";
export const DEFAULT_CREATE_WATCHLIST_NAME = "";

const WATCHLIST_MODAL_VERTICAL_MARGIN = 8;
const WATCHLIST_MIN_MODAL_HEIGHT = 670;
const WATCHLIST_FIXED_CONTENT_HEIGHT = 290;

const DEFAULT_SORT_CONFIG = {
  key: "cusip",
  direction: "desc",
};

const CREATE_WATCHLIST_REQUEST_ID = 0;

const getFetchCriteria = (appliedCriteria) => {
  if (!appliedCriteria?.hasCriteria) {
    return null;
  }

  return appliedCriteria.filters;
};

const normalizeWatchlistType = (value) =>
  `${value || ""}`.toLowerCase() === WATCHLIST_TYPE.DYNAMIC
    ? WATCHLIST_TYPE.DYNAMIC
    : WATCHLIST_TYPE.STATIC;

const resolveWatchlistRequestId = (watchlistId, isCreateDraft = false) => {
  if (isCreateDraft) {
    return CREATE_WATCHLIST_REQUEST_ID;
  }

  if (watchlistId === null || watchlistId === undefined || watchlistId === "") {
    return null;
  }

  return watchlistId;
};

const toApiSortKey = (sortKey = "") => sortKey;

const sanitizeBondFilterValue = (value = "") =>
  `${value}`
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const buildAllFetchKey = ({ fetchConfig, token, appliedCriteriaKey }) =>
  JSON.stringify({
    ...fetchConfig,
    token,
    appliedCriteriaKey: appliedCriteriaKey || "",
  });

const buildMyFetchKey = ({ fetchConfig, token, appliedCriteriaKey }) =>
  JSON.stringify({
    page: fetchConfig.watchlistItemsPage,
    size: fetchConfig.watchlistItemsPageSize,
    sortBy: fetchConfig.watchlistItemsSortBy,
    sortDirection: fetchConfig.watchlistItemsSortDirection,
    searchString: fetchConfig.watchlistItemsSearchString,
    watchlistId: fetchConfig.watchlistItemsWatchlistId,
    criteria: fetchConfig.criteria,
    token,
    appliedCriteriaKey: appliedCriteriaKey || "",
  });

const getWatchlistItemsPagination = (response) =>
  response?.itemsPagination ?? response?.pagination ?? null;

const mergePersistedCusipsWithPendingChanges = ({
  nextPersistedCusips = [],
  currentPersistedCusips = [],
  currentLocalCusips = [],
}) => {
  const nextPersisted = normalizeCusipList(nextPersistedCusips);
  const currentPersisted = normalizeCusipList(currentPersistedCusips);
  const currentLocal = normalizeCusipList(currentLocalCusips);
  const currentPersistedSet = new Set(currentPersisted);
  const currentLocalSet = new Set(currentLocal);
  const pendingAdds = currentLocal.filter(
    (cusip) => !currentPersistedSet.has(cusip),
  );
  const pendingRemovedSet = new Set(
    currentPersisted.filter((cusip) => !currentLocalSet.has(cusip)),
  );

  return normalizeCusipList([
    ...nextPersisted.filter((cusip) => !pendingRemovedSet.has(cusip)),
    ...pendingAdds,
  ]);
};

const getWatchlistLayoutMetrics = () => {
  if (typeof window === "undefined") {
    return {
      modalHeight: WATCHLIST_MIN_MODAL_HEIGHT,
      tableViewportHeight: WATCHLIST_TABLE_VIEWPORT_HEIGHT,
    };
  }

  const modalHeight = Math.max(
    WATCHLIST_MIN_MODAL_HEIGHT,
    window.innerHeight - WATCHLIST_MODAL_VERTICAL_MARGIN,
  );

  const tableViewportHeight = Math.max(
    WATCHLIST_TABLE_VIEWPORT_HEIGHT,
    modalHeight - WATCHLIST_FIXED_CONTENT_HEIGHT,
  );

  return {
    modalHeight,
    tableViewportHeight,
  };
};

export const useMAWatchlistPopupState = ({
  open,
  onClose,
  token,
  onError,
  onSuccess,
  onInfo,
  initialWatchlistId = null,
  createMode = false,
}) => {
  const [layoutMetrics, setLayoutMetrics] = useState(() =>
    getWatchlistLayoutMetrics(),
  );
  const { modalHeight } = layoutMetrics;

  const {
    watchlists,
    currentWatchlist,
    secMasterBonds,
    secMasterByCusip,
    loading,
    secMasterLoading,
    saving,
    secMasterPagination,
    itemsPagination,
    error,
    secMasterError,
    watchlistItemsError,
    clearError,
    clearSecMasterError,
    clearWatchlistItemsError,
    setError,
    setCurrentWatchlist,
    upsertWatchlist,
    clearLoadedWatchlistPages,
    loadSecMasterPage,
    loadWatchlistData,
    loadWatchlistItemsPage,
    saveWatchlist,
    retryFailedDynamicWatchlist,
    clearCurrentWatchlist,
    reset: resetWatchlistStore,
  } = useMAWatchlistStore();

  const {
    userFilterData,
    appliedFilterData,
    defaultFilterData,
    filterOptions,
    loadingFilterDefaults,
    filterDefaultsError,
    clearFilterDefaultsError,
    replaceUserFilterData,
    replaceAppliedFilterData,
    setUserFilterField,
    loadFilterDefaults,
    resetUserFilterData,
    resetUserFilterDataKeepBondType,
  } = useMAWatchlistFilterStore();
  const activeFilterBondType = userFilterData.bondType;

  const [activeTab, setActiveTab] = useState(WATCHLIST_TABS.ALL);
  const [watchlistType, setWatchlistType] = useState(WATCHLIST_TYPE.STATIC);
  const [bondFilter, setBondFilter] = useState("");
  const [forceExpandFilter, setForceExpandFilter] = useState(false);

  const resetForceExpandFilter = useCallback(() => {
    setForceExpandFilter(false);
  }, []);
  const [watchlistName, setWatchlistName] = useState("");
  const [serverWatchlistCusips, setServerWatchlistCusips] = useState([]);
  const [persistedWatchlistCusips, setPersistedWatchlistCusips] = useState([]);
  const [persistedWatchlistTotalCount, setPersistedWatchlistTotalCount] =
    useState(0);
  const [localWatchlistCusips, setLocalWatchlistCusips] = useState([]);
  const persistedWatchlistCusipsRef = useRef([]);
  const localWatchlistCusipsRef = useRef([]);
  const [allTablePage, setAllTablePage] = useState(0);
  const [allRowsPerPage, setAllRowsPerPage] = useState(
    WATCHLIST_DEFAULT_ROWS_PER_PAGE,
  );
  const [myTablePage, setMyTablePage] = useState(0);
  const [myRowsPerPage, setMyRowsPerPage] = useState(
    WATCHLIST_DEFAULT_ROWS_PER_PAGE,
  );
  const [allSortConfig, setAllSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [mySortConfig, setMySortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [hasLoadedAllTabCount, setHasLoadedAllTabCount] = useState(false);
  const [hasLoadedMyTabCount, setHasLoadedMyTabCount] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isDeleteAllConfirmOpen, setIsDeleteAllConfirmOpen] = useState(false);
  const [isDynamicToStaticConfirmOpen, setIsDynamicToStaticConfirmOpen] =
    useState(false);
  const [
    isDynamicSaveCriteriaWarningOpen,
    setIsDynamicSaveCriteriaWarningOpen,
  ] = useState(false);
  const [isDynamicNoCriteriaErrorOpen, setIsDynamicNoCriteriaErrorOpen] =
    useState(false);
  const [clearingWatchlist, setClearingWatchlist] = useState(false);
  const [
    hasSwitchedBackendDynamicToStatic,
    setHasSwitchedBackendDynamicToStatic,
  ] = useState(false);
  const hasPersistedWatchlistMutationRef = useRef(false);
  const staticLocalWatchlistCusipsRef = useRef(null);
  const lastAllFetchKeyRef = useRef("");
  const lastMyFetchKeyRef = useRef("");
  const isInitializingRef = useRef(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isFirstFetchStarted, setIsFirstFetchStarted] = useState(false);
  const userChangedWatchlistTypeRef = useRef(false);
  const skipNextFilterDefaultsLoadRef = useRef(false);
  const watchlistSelectionRequestRef = useRef(0);
  const criteriaApplyRequestRef = useRef(0);
  const hasInitializedOpenRef = useRef(false);

  const rowsPerPageOptions = [15, 30, 50];
  const normalizedBondFilter = bondFilter.trim();
  const isFilterActive = Boolean(normalizedBondFilter);
  const activeWatchlistId = currentWatchlist?.watchlistId ?? null;
  const isCreateWatchlistDraft = activeWatchlistId === CREATE_WATCHLIST_ID;
  const isBackendDynamicCurrentWatchlist =
    !isCreateWatchlistDraft
    && isBackendDynamicWatchlist(currentWatchlist);
  const isProcessingWatchlistLocked =
    !isCreateWatchlistDraft && isWatchlistProcessing(currentWatchlist);
  const isFailedWatchlist =
    !isCreateWatchlistDraft && isWatchlistFailed(currentWatchlist);
  const canRetryFailedBackendDynamicWatchlist =
    !isCreateWatchlistDraft && canRetryFailedDynamicWatchlist(currentWatchlist);
  const processingWatchlistMessage = isProcessingWatchlistLocked
    ? getWatchlistProcessingMessage(
      getWatchlistDisplayName(currentWatchlist) || "This watchlist"
    )
    : "";
  const failedWatchlistMessage = isFailedWatchlist
    ? getWatchlistRetryMessage(
      getWatchlistDisplayName(currentWatchlist) || "This watchlist"
    )
    : "";
  const selectableWatchlists = useMemo(() => {
    if (!isCreateWatchlistDraft) {
      return watchlists;
    }

    return [
      {
        watchlistId: CREATE_WATCHLIST_ID,
        watchlistName: watchlistName || DEFAULT_CREATE_WATCHLIST_NAME,
        watchlistType,
      },
      ...watchlists,
    ];
  }, [isCreateWatchlistDraft, watchlistName, watchlistType, watchlists]);
  const isDynamicWatchlist = watchlistType === WATCHLIST_TYPE.DYNAMIC;
  const normalizedUserFilterData = useMemo(
    () => userFilterData,
    [userFilterData],
  );
  const normalizedAppliedFilterData = useMemo(
    () => appliedFilterData,
    [appliedFilterData],
  );
  const userCriteria = useMemo(
    () => buildWatchlistCriteriaState(normalizedUserFilterData, watchlistType),
    [normalizedUserFilterData, watchlistType],
  );
  const appliedCriteria = useMemo(
    () =>
      buildWatchlistCriteriaState(normalizedAppliedFilterData, watchlistType),
    [normalizedAppliedFilterData, watchlistType],
  );
  const userCriteriaKey = useMemo(
    () => getWatchlistCriteriaComparisonKey(userCriteria),
    [userCriteria],
  );
  const appliedCriteriaKey = useMemo(
    () => getWatchlistCriteriaComparisonKey(appliedCriteria),
    [appliedCriteria],
  );
  const initialAppliedCriteriaKeyRef = useRef(appliedCriteriaKey);
  const appliedFetchCriteria = useMemo(
    () => getFetchCriteria(appliedCriteria),
    [appliedCriteria],
  );
  const hasUnappliedCriteriaChanges = userCriteriaKey !== appliedCriteriaKey;
  const isApplyCriteriaDisabled = !hasUnappliedCriteriaChanges;
  const isClearFiltersDisabled = !appliedCriteria?.hasCriteria;
  const hasAppliedDynamicCriteria = Boolean(
    appliedCriteria?.hasCriteria && isDynamicWatchlist,
  );
  // Dynamic watchlists are hard-capped: if the applied characteristics match more
  // than MA_DYNAMIC_WATCHLIST_CUSIP_LIMIT bonds, the watchlist cannot be saved.
  const isExceedingDynamicLimit =
    isDynamicWatchlist
    && hasAppliedDynamicCriteria
    && hasLoadedAllTabCount
    && !secMasterLoading
    && Number(secMasterPagination?.totalElements) > MA_DYNAMIC_WATCHLIST_CUSIP_LIMIT;
  const shouldIgnorePartOfWatchlist = hasSwitchedBackendDynamicToStatic;
  const isReadOnlyBackendDynamicWatchlistView =
    shouldIgnorePartOfWatchlist && !isDynamicWatchlist;

  useEffect(() => {
    persistedWatchlistCusipsRef.current = persistedWatchlistCusips;
  }, [persistedWatchlistCusips]);

  useEffect(() => {
    localWatchlistCusipsRef.current = localWatchlistCusips;
  }, [localWatchlistCusips]);

  const handleFilterFieldChange = useCallback(
    (name, value) => {
      if (isProcessingWatchlistLocked) {
        return;
      }

      setUserFilterField(name, value);
      if (name === "bondType" && value) {
        setForceExpandFilter(true);
      }
    },
    [isProcessingWatchlistLocked, setUserFilterField]
  );

  const clearPopupState = useCallback(() => {
    setWatchlistName("");
    setServerWatchlistCusips([]);
    setPersistedWatchlistCusips([]);
    setPersistedWatchlistTotalCount(0);
    setLocalWatchlistCusips([]);
    setBondFilter("");
    setActiveTab(WATCHLIST_TABS.ALL);
    setWatchlistType(WATCHLIST_TYPE.STATIC);
    replaceUserFilterData();
    replaceAppliedFilterData();
    setAllTablePage(0);
    setAllRowsPerPage(WATCHLIST_DEFAULT_ROWS_PER_PAGE);
    setMyTablePage(0);
    setMyRowsPerPage(WATCHLIST_DEFAULT_ROWS_PER_PAGE);
    setAllSortConfig(DEFAULT_SORT_CONFIG);
    setMySortConfig(DEFAULT_SORT_CONFIG);
    setHasLoadedAllTabCount(false);
    setHasLoadedMyTabCount(false);
    setIsCloseConfirmOpen(false);
    setIsDeleteAllConfirmOpen(false);
    setIsDynamicToStaticConfirmOpen(false);
    setIsDynamicSaveCriteriaWarningOpen(false);
    setIsDynamicNoCriteriaErrorOpen(false);
    setClearingWatchlist(false);
    setHasSwitchedBackendDynamicToStatic(false);
    hasPersistedWatchlistMutationRef.current = false;
    staticLocalWatchlistCusipsRef.current = null;
    lastAllFetchKeyRef.current = "";
    lastMyFetchKeyRef.current = "";
    isInitializingRef.current = false;
    setIsInitializing(false);
    setIsFirstFetchStarted(false);
    userChangedWatchlistTypeRef.current = false;
    hasInitializedOpenRef.current = false;
    watchlistSelectionRequestRef.current += 1;
    criteriaApplyRequestRef.current += 1;
    resetWatchlistStore();
  }, [replaceAppliedFilterData, replaceUserFilterData, resetWatchlistStore]);

  const syncFiltersForWatchlist = useCallback(
    async (watchlist, requestId, isBackgroundRefresh = false) => {
      const prefilledCharacteristics = parseWatchlistCharacteristics(
        watchlist?.characteristics,
      );

      if (prefilledCharacteristics && !userChangedWatchlistTypeRef.current) {
        const prefilledBondType = prefilledCharacteristics.bondType || "";

        await loadFilterDefaults(token, prefilledBondType, {
          skipUserFilterDataUpdate: true,
        });

        if (watchlistSelectionRequestRef.current !== requestId) {
          return { aborted: true };
        }

        const prefilledFilterData = {
          ...prefilledCharacteristics,
          bondType: prefilledBondType,
        };

        replaceUserFilterData(prefilledFilterData);
        replaceAppliedFilterData(prefilledFilterData);

        const nextCriteria = buildWatchlistCriteriaState(
          useMAWatchlistFilterStore.getState().appliedFilterData,
          WATCHLIST_TYPE.DYNAMIC,
        );
        initialAppliedCriteriaKeyRef.current =
          getWatchlistCriteriaComparisonKey(nextCriteria);

        skipNextFilterDefaultsLoadRef.current = true;

        return {
          appliedCriteria: nextCriteria,
        };
      }

      const nextWatchlistType = normalizeWatchlistType(
        watchlist?.watchlistType,
      );

      await loadFilterDefaults(token, undefined, {
        skipUserFilterDataUpdate: isBackgroundRefresh,
      });

      if (watchlistSelectionRequestRef.current !== requestId) {
        return { aborted: true };
      }

      if (nextWatchlistType === WATCHLIST_TYPE.DYNAMIC) {
        skipNextFilterDefaultsLoadRef.current = true;
      }

      // Do NOT replace the applied filter data unconditionally if user currently has active ones that shouldn't be wiped.
      const currentApplied = useMAWatchlistFilterStore.getState().appliedFilterData;
      const existingCriteria = buildWatchlistCriteriaState(currentApplied, nextWatchlistType);

      if (!existingCriteria?.hasCriteria) {
        replaceAppliedFilterData();
      }

      const emptyCriteria = buildWatchlistCriteriaState(
        useMAWatchlistFilterStore.getState().appliedFilterData,
        nextWatchlistType,
      );
      return {
        appliedCriteria: emptyCriteria,
      };
    },
    [
      loadFilterDefaults,
      replaceAppliedFilterData,
      replaceUserFilterData,
      token,
    ],
  );

  const initializeWatchlistSelection = useCallback(
    async ({
      watchlist,
      watchlistRequestId,
      initialFetchConfig,
      initialMyFetchConfig,
      requestId,
      isBackgroundRefresh = false,
    }) => {
      if (isWatchlistProcessing(watchlist)) {
        clearLoadedWatchlistPages();
        setHasLoadedAllTabCount(false);
        setHasLoadedMyTabCount(false);
        setServerWatchlistCusips([]);
        if (!isBackgroundRefresh) {
          setPersistedWatchlistCusips([]);
          setLocalWatchlistCusips([]);
        }
        lastAllFetchKeyRef.current = "";
        lastMyFetchKeyRef.current = "";

        return null;
      }

      const filterSyncState = await syncFiltersForWatchlist(watchlist, requestId, isBackgroundRefresh);

      if (
        filterSyncState?.aborted ||
        watchlistSelectionRequestRef.current !== requestId
      ) {
        return null;
      }

      const criteria = getFetchCriteria(filterSyncState?.appliedCriteria);
      const initialAppliedCriteriaKey = getWatchlistCriteriaComparisonKey(
        filterSyncState?.appliedCriteria,
      );
      const allFetchConfig = {
        ...initialFetchConfig,
        watchlistId: watchlistRequestId,
        criteria,
      };
      const myFetchConfig = {
        ...initialMyFetchConfig,
        watchlistItemsWatchlistId: watchlistRequestId,
        criteria,
      };

      setIsFirstFetchStarted(true);

      const p1 = loadSecMasterPage(token, allFetchConfig)
        .then((res) => {
          if (watchlistSelectionRequestRef.current === requestId) {
            setHasLoadedAllTabCount(true);
          }
          return res;
        })
        .catch((err) => {
          if (watchlistSelectionRequestRef.current === requestId) {
            lastAllFetchKeyRef.current = "";
          }
          throw err;
        });

      const p2 = loadWatchlistItemsPage(token, myFetchConfig)
        .then((res) => {
          if (watchlistSelectionRequestRef.current === requestId) {
            setHasLoadedMyTabCount(true);
          }
          return res;
        })
        .catch((err) => {
          if (watchlistSelectionRequestRef.current === requestId) {
            lastMyFetchKeyRef.current = "";
          }
          throw err;
        });

      const [, watchlistItemsState] = await Promise.all([p1, p2]);

      if (watchlistSelectionRequestRef.current !== requestId) {
        return null;
      }

      const allFetchKey = buildAllFetchKey({
        fetchConfig: allFetchConfig,
        token,
        appliedCriteriaKey: initialAppliedCriteriaKey,
      });

      const myFetchKey = buildMyFetchKey({
        fetchConfig: myFetchConfig,
        token,
        appliedCriteriaKey: initialAppliedCriteriaKey,
      });

      const initialPageWatchlistCusips = normalizeCusipList(
        watchlistItemsState?.watchlistCusips || [],
      );
      const initialItemsPagination =
        getWatchlistItemsPagination(watchlistItemsState);
      const initialPersistedWatchlistTotalCount = Number(
        initialItemsPagination?.totalElements,
      );
      const shouldPreservePendingStaticChanges =
        isBackgroundRefresh &&
        normalizeWatchlistType(watchlist?.watchlistType) ===
          WATCHLIST_TYPE.STATIC;
      const nextLocalWatchlistCusips = shouldPreservePendingStaticChanges
        ? mergePersistedCusipsWithPendingChanges({
            nextPersistedCusips: initialPageWatchlistCusips,
            currentPersistedCusips: persistedWatchlistCusipsRef.current,
            currentLocalCusips: localWatchlistCusipsRef.current,
          })
        : initialPageWatchlistCusips;

      setServerWatchlistCusips(initialPageWatchlistCusips);
      setPersistedWatchlistCusips(initialPageWatchlistCusips);
      setPersistedWatchlistTotalCount(
        Number.isFinite(initialPersistedWatchlistTotalCount)
          ? initialPersistedWatchlistTotalCount
          : initialPageWatchlistCusips.length,
      );
      setLocalWatchlistCusips(nextLocalWatchlistCusips);
      lastAllFetchKeyRef.current = allFetchKey;
      lastMyFetchKeyRef.current = myFetchKey;
      setHasLoadedAllTabCount(true);
      setHasLoadedMyTabCount(true);

      return {
        appliedCriteria: filterSyncState?.appliedCriteria || null,
      };
    },
    [
      clearLoadedWatchlistPages,
      loadSecMasterPage,
      loadWatchlistItemsPage,
      syncFiltersForWatchlist,
      token,
    ]
  );

  const applyFreshWatchlistSelection = useCallback(
    (nextWatchlist = null) => {
      const nextWatchlistId = nextWatchlist?.watchlistId;
      const nextWatchlistType = normalizeWatchlistType(
        nextWatchlist?.watchlistType,
      );
      const nextWatchlistName =
        nextWatchlistId === CREATE_WATCHLIST_ID
          ? DEFAULT_CREATE_WATCHLIST_NAME
          : nextWatchlist?.watchlistName || "";

      setWatchlistName(nextWatchlistName);
      setWatchlistType(nextWatchlistType);
      replaceAppliedFilterData();
      criteriaApplyRequestRef.current += 1;
      setActiveTab(WATCHLIST_TABS.ALL);
      setHasSwitchedBackendDynamicToStatic(false);
      setIsDynamicToStaticConfirmOpen(false);
      setIsDynamicSaveCriteriaWarningOpen(false);
      setIsDynamicNoCriteriaErrorOpen(false);
      resetUserFilterData();
    },
    [replaceAppliedFilterData, resetUserFilterData],
  );

  const handleWatchlistSelect = useCallback(
    (nextWatchlistId) => {
      if (nextWatchlistId === CREATE_WATCHLIST_ID) {
        return;
      }

      const normalizedId = Number(nextWatchlistId);

      if (!normalizedId || normalizedId === activeWatchlistId) {
        return;
      }

      const nextWatchlist = watchlists.find(
        (watchlist) => Number(watchlist?.watchlistId) === normalizedId,
      );

      if (!nextWatchlist) {
        return;
      }

      const shouldLoadSelectedWatchlist = !isWatchlistProcessing(nextWatchlist);
      const requestId = watchlistSelectionRequestRef.current + 1;
      watchlistSelectionRequestRef.current = requestId;
      isInitializingRef.current = shouldLoadSelectedWatchlist;
      setIsInitializing(shouldLoadSelectedWatchlist);
      setIsFirstFetchStarted(false);
      userChangedWatchlistTypeRef.current = false;
      setCurrentWatchlist(nextWatchlist);
      applyFreshWatchlistSelection(nextWatchlist);
      setServerWatchlistCusips([]);
      setPersistedWatchlistCusips([]);
      setPersistedWatchlistTotalCount(0);
      setLocalWatchlistCusips([]);
      setAllTablePage(0);
      setMyTablePage(0);
      setBondFilter("");
      lastAllFetchKeyRef.current = "";
      lastMyFetchKeyRef.current = "";
      staticLocalWatchlistCusipsRef.current = null;
      hasPersistedWatchlistMutationRef.current = false;

      const initialFetchConfig = {
        page: 0,
        size: WATCHLIST_DEFAULT_ROWS_PER_PAGE,
        sortBy: DEFAULT_SORT_CONFIG.key,
        sortDirection: DEFAULT_SORT_CONFIG.direction.toUpperCase(),
        searchString: "",
      };

      const initialMyFetchConfig = {
        watchlistItemsPage: 0,
        watchlistItemsPageSize: WATCHLIST_DEFAULT_ROWS_PER_PAGE,
        watchlistItemsSortBy: DEFAULT_SORT_CONFIG.key,
        watchlistItemsSortDirection:
          DEFAULT_SORT_CONFIG.direction.toUpperCase(),
        watchlistItemsSearchString: "",
      };

      const nextWatchlistRequestId = resolveWatchlistRequestId(
        nextWatchlist.watchlistId,
      );

      initializeWatchlistSelection({
        watchlist: nextWatchlist,
        watchlistRequestId: nextWatchlistRequestId,
        initialFetchConfig,
        initialMyFetchConfig,
        requestId,
      })
        .catch((loadError) => {
          if (watchlistSelectionRequestRef.current !== requestId) {
            return;
          }

          lastAllFetchKeyRef.current = "";
          lastMyFetchKeyRef.current = "";
          setHasLoadedAllTabCount(false);
          setHasLoadedMyTabCount(false);
        })
        .finally(() => {
          if (watchlistSelectionRequestRef.current === requestId) {
            isInitializingRef.current = false;
            setIsInitializing(false);
          }
        });
    },
    [
      activeWatchlistId,
      initializeWatchlistSelection,
      onError,
      setCurrentWatchlist,
      watchlists,
      applyFreshWatchlistSelection,
      setServerWatchlistCusips,
      setPersistedWatchlistCusips,
      setLocalWatchlistCusips,
      setAllTablePage,
      setMyTablePage,
    ],
  );

  const handleCreateWatchlistDraft = useCallback(() => {
    watchlistSelectionRequestRef.current += 1;
    isInitializingRef.current = false;
    setIsInitializing(false);
    setIsFirstFetchStarted(false);
    const createWatchlist = {
      watchlistId: CREATE_WATCHLIST_ID,
      watchlistName: DEFAULT_CREATE_WATCHLIST_NAME,
      watchlistType: WATCHLIST_TYPE.STATIC,
    };

    setCurrentWatchlist(createWatchlist);
    applyFreshWatchlistSelection(createWatchlist);
    setPersistedWatchlistCusips([]);
    setPersistedWatchlistTotalCount(0);
    setServerWatchlistCusips([]);
    setLocalWatchlistCusips([]);
    setAllTablePage(0);
    setMyTablePage(0);
    setBondFilter("");
    setHasLoadedAllTabCount(true);
    setHasLoadedMyTabCount(true);
    lastAllFetchKeyRef.current = "";
    lastMyFetchKeyRef.current = "";
    staticLocalWatchlistCusipsRef.current = null;
    setHasSwitchedBackendDynamicToStatic(false);
    setIsDynamicToStaticConfirmOpen(false);
    hasPersistedWatchlistMutationRef.current = false;
  }, [applyFreshWatchlistSelection, setCurrentWatchlist]);

  const applyWatchlistTypeChange = useCallback(
    (
      nextWatchlistType,
      { markSwitchedFromBackendDynamicToStatic = false } = {},
    ) => {
      if (nextWatchlistType === watchlistType) {
        return;
      }

      if (watchlistType === WATCHLIST_TYPE.STATIC) {
        staticLocalWatchlistCusipsRef.current = localWatchlistCusips;
        setLocalWatchlistCusips(persistedWatchlistCusips);
      } else if (nextWatchlistType === WATCHLIST_TYPE.STATIC) {
        if (markSwitchedFromBackendDynamicToStatic) {
          setPersistedWatchlistCusips([]);
          setPersistedWatchlistTotalCount(0);
          setLocalWatchlistCusips([]);
          staticLocalWatchlistCusipsRef.current = null;
        } else {
          setLocalWatchlistCusips(
            staticLocalWatchlistCusipsRef.current ?? persistedWatchlistCusips,
          );
          staticLocalWatchlistCusipsRef.current = null;
        }
      }

      userChangedWatchlistTypeRef.current = true;
      setWatchlistType(nextWatchlistType);
      setIsDynamicSaveCriteriaWarningOpen(false);
      setIsDynamicNoCriteriaErrorOpen(false);
      setAllTablePage(0);
      setMyTablePage(0);
      lastAllFetchKeyRef.current = "";
      lastMyFetchKeyRef.current = "";
      criteriaApplyRequestRef.current += 1;

      if (markSwitchedFromBackendDynamicToStatic) {
        setHasSwitchedBackendDynamicToStatic(true);
      }
    },
    [localWatchlistCusips, persistedWatchlistCusips, watchlistType],
  );

  const handleWatchlistTypeChange = useCallback(
    (nextWatchlistType) => {
      if (isProcessingWatchlistLocked) {
      return;
    }
      const isDynamicToStaticFromBackendDynamic =
        watchlistType === WATCHLIST_TYPE.DYNAMIC &&
        nextWatchlistType === WATCHLIST_TYPE.STATIC &&
        isBackendDynamicCurrentWatchlist;

      if (isDynamicToStaticFromBackendDynamic) {
        setIsDynamicToStaticConfirmOpen(true);
        return;
      }

      applyWatchlistTypeChange(nextWatchlistType);
    },
    [
    applyWatchlistTypeChange,
    isBackendDynamicCurrentWatchlist,
    isProcessingWatchlistLocked,
    watchlistType,
  ],
  );

  const handleDismissDynamicToStaticConfirmation = useCallback(() => {
    if (saving || clearingWatchlist) {
      return;
    }

    setIsDynamicToStaticConfirmOpen(false);
  }, [clearingWatchlist, saving]);

  const handleConfirmDynamicToStaticConfirmation = useCallback(() => {
    setIsDynamicToStaticConfirmOpen(false);
    applyWatchlistTypeChange(WATCHLIST_TYPE.STATIC, {
      markSwitchedFromBackendDynamicToStatic: true,
    });
  }, [applyWatchlistTypeChange]);

  useEffect(() => {
    if (!open || typeof window === "undefined") {
      return undefined;
    }

    const updateLayoutMetrics = () => {
      setLayoutMetrics(getWatchlistLayoutMetrics());
    };

    updateLayoutMetrics();
    window.addEventListener("resize", updateLayoutMetrics);

    return () => {
      window.removeEventListener("resize", updateLayoutMetrics);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (hasInitializedOpenRef.current) {
      return undefined;
    }

    hasInitializedOpenRef.current = true;

    let isMounted = true;

    setActiveTab(WATCHLIST_TABS.ALL);
    setBondFilter("");
    setAllTablePage(0);
    setAllRowsPerPage(WATCHLIST_DEFAULT_ROWS_PER_PAGE);
    setMyTablePage(0);
    setMyRowsPerPage(WATCHLIST_DEFAULT_ROWS_PER_PAGE);
    setAllSortConfig(DEFAULT_SORT_CONFIG);
    setMySortConfig(DEFAULT_SORT_CONFIG);
    setHasLoadedAllTabCount(false);
    setHasLoadedMyTabCount(false);
    clearError();
    lastAllFetchKeyRef.current = "";
    lastMyFetchKeyRef.current = "";
    isInitializingRef.current = true;
    setIsInitializing(true);
    setIsFirstFetchStarted(false);
    userChangedWatchlistTypeRef.current = false;

    const initializeWatchlist = async () => {
      try {
        const initialFetchConfig = {
          page: 0,
          size: WATCHLIST_DEFAULT_ROWS_PER_PAGE,
          sortBy: DEFAULT_SORT_CONFIG.key,
          sortDirection: DEFAULT_SORT_CONFIG.direction.toUpperCase(),
          searchString: "",
        };

        const initialMyFetchConfig = {
          watchlistItemsPage: 0,
          watchlistItemsPageSize: WATCHLIST_DEFAULT_ROWS_PER_PAGE,
          watchlistItemsSortBy: DEFAULT_SORT_CONFIG.key,
          watchlistItemsSortDirection:
            DEFAULT_SORT_CONFIG.direction.toUpperCase(),
          watchlistItemsSearchString: "",
        };

        const requestId = watchlistSelectionRequestRef.current + 1;
        watchlistSelectionRequestRef.current = requestId;

        const data = await loadWatchlistData(token, {
          ...initialMyFetchConfig,
          initialWatchlistId: createMode ? null : initialWatchlistId,
          skipWatchlistItems: true,
        });
        const createWatchlist = {
          watchlistId: CREATE_WATCHLIST_ID,
          watchlistName: DEFAULT_CREATE_WATCHLIST_NAME,
          watchlistType: WATCHLIST_TYPE.STATIC,
        };
        const resolvedCurrentWatchlist = createMode
          ? createWatchlist
          : (data?.currentWatchlist ?? null);
        const resolvedWatchlistId = createMode
          ? null
          : (resolvedCurrentWatchlist?.watchlistId ?? null);
        const resolvedWatchlistRequestId = resolveWatchlistRequestId(
          resolvedWatchlistId,
          createMode,
        );

        if (!isMounted || watchlistSelectionRequestRef.current !== requestId) {
          return;
        }

        if (createMode) {
          setCurrentWatchlist(createWatchlist);
        }

        if (!isMounted) {
          return;
        }

        setWatchlistName(resolvedCurrentWatchlist?.watchlistName ?? "");
        if (!userChangedWatchlistTypeRef.current) {
          setWatchlistType(
            normalizeWatchlistType(resolvedCurrentWatchlist?.watchlistType),
          );
        }
        setHasSwitchedBackendDynamicToStatic(false);

        await initializeWatchlistSelection({
          watchlist: resolvedCurrentWatchlist,
          watchlistRequestId: resolvedWatchlistRequestId,
          initialFetchConfig,
          initialMyFetchConfig,
          requestId,
        });
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setWatchlistName("");
        setServerWatchlistCusips([]);
        setPersistedWatchlistCusips([]);
        setPersistedWatchlistTotalCount(0);
        setLocalWatchlistCusips([]);
        setHasSwitchedBackendDynamicToStatic(false);
        setHasLoadedAllTabCount(false);
        setHasLoadedMyTabCount(false);
      } finally {
        isInitializingRef.current = false;
        setIsInitializing(false);
      }
    };

    initializeWatchlist();

    return () => {
      isMounted = false;
    };
  }, [
    clearError,
    loadSecMasterPage,
    loadWatchlistData,
    loadWatchlistItemsPage,
    initializeWatchlistSelection,
    onError,
    open,
    token,
    initialWatchlistId,
    createMode,
    setCurrentWatchlist,
  ]);

  useEffect(() => {
    if (open) {
      return;
    }

    hasInitializedOpenRef.current = false;
    clearPopupState();
  }, [clearPopupState, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleWatchlistStatusUpdate = (event) => {
      const statusPayload = getWatchlistStatusUpdatePayload(event?.detail);

      if (!statusPayload) {
        return;
      }

      const currentWatchlistId = currentWatchlist?.watchlistId ?? null;
      if (String(statusPayload.watchlistId) !== String(currentWatchlistId ?? "")) {
        return;
      }

      const nextStatus = normalizeWatchlistStatus(statusPayload.status);
      const previousStatus = normalizeWatchlistStatus(currentWatchlist?.status);
      const nextWatchlist = upsertWatchlist({
        ...currentWatchlist,
        ...statusPayload,
        watchlistId: currentWatchlistId,
        status: nextStatus,
      });

      if (nextWatchlist?.watchlistName) {
        setWatchlistName(nextWatchlist.watchlistName);
      }

      if (previousStatus === nextStatus) {
        return;
      }

      if (nextStatus === WATCHLIST_STATUS.PROCESSING) {
        clearLoadedWatchlistPages();
        setHasLoadedAllTabCount(false);
        setHasLoadedMyTabCount(false);
        setServerWatchlistCusips([]);
        setIsFirstFetchStarted(false);
        lastAllFetchKeyRef.current = "";
        lastMyFetchKeyRef.current = "";
        return;
      }

      const requestId = watchlistSelectionRequestRef.current + 1;
      watchlistSelectionRequestRef.current = requestId;
      isInitializingRef.current = true;
      setIsInitializing(true);
      setIsFirstFetchStarted(false);
      setHasLoadedAllTabCount(false);
      setHasLoadedMyTabCount(false);
      lastAllFetchKeyRef.current = "";
      lastMyFetchKeyRef.current = "";

      const initialFetchConfig = {
        page: 0,
        size: WATCHLIST_DEFAULT_ROWS_PER_PAGE,
        sortBy: DEFAULT_SORT_CONFIG.key,
        sortDirection: DEFAULT_SORT_CONFIG.direction.toUpperCase(),
        searchString: "",
      };

      const initialMyFetchConfig = {
        watchlistItemsPage: 0,
        watchlistItemsPageSize: WATCHLIST_DEFAULT_ROWS_PER_PAGE,
        watchlistItemsSortBy: DEFAULT_SORT_CONFIG.key,
        watchlistItemsSortDirection: DEFAULT_SORT_CONFIG.direction.toUpperCase(),
        watchlistItemsSearchString: "",
      };

      initializeWatchlistSelection({
        watchlist: nextWatchlist,
        watchlistRequestId: resolveWatchlistRequestId(nextWatchlist?.watchlistId),
        initialFetchConfig,
        initialMyFetchConfig,
        requestId,
        isBackgroundRefresh: true,
      })
        .catch(() => {
          if (watchlistSelectionRequestRef.current !== requestId) {
            return;
          }

          setHasLoadedAllTabCount(false);
          setHasLoadedMyTabCount(false);
        })
        .finally(() => {
          if (watchlistSelectionRequestRef.current === requestId) {
            isInitializingRef.current = false;
            setIsInitializing(false);
          }
        });

      if (nextStatus === WATCHLIST_STATUS.FAILED) {
        onError?.(getWatchlistRetryMessage());
      }
    };

    window.addEventListener("maWatchlistStatusUpdate", handleWatchlistStatusUpdate);
    return () => {
      window.removeEventListener("maWatchlistStatusUpdate", handleWatchlistStatusUpdate);
    };
  }, [
    clearLoadedWatchlistPages,
    currentWatchlist,
    initializeWatchlistSelection,
    onError,
    open,
    upsertWatchlist,
  ]);

  useEffect(() => {
    if (!open || isInitializingRef.current) {
      // Clear the skipped flag if we're ignoring this update due to initialization
      if (skipNextFilterDefaultsLoadRef.current) {
        skipNextFilterDefaultsLoadRef.current = false;
      }
      return;
    }

    if (skipNextFilterDefaultsLoadRef.current) {
      skipNextFilterDefaultsLoadRef.current = false;
      return;
    }

    loadFilterDefaults(token, activeFilterBondType).catch((loadError) => {
      // internal state holds error
    });
  }, [activeFilterBondType, loadFilterDefaults, open, token]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (isInitializingRef.current || isProcessingWatchlistLocked) {
      return undefined;
    }

    const secMasterPage = isFilterActive ? 0 : allTablePage;
    const watchlistRequestId = resolveWatchlistRequestId(
      activeWatchlistId,
      isCreateWatchlistDraft,
    );
    const allFetchConfig = {
      page: secMasterPage,
      size: allRowsPerPage,
      sortBy: toApiSortKey(allSortConfig.key),
      sortDirection: allSortConfig.direction.toUpperCase(),
      searchString: normalizedBondFilter,
      watchlistId: watchlistRequestId,
      criteria: appliedFetchCriteria,
    };

    const allFetchKey = buildAllFetchKey({
      fetchConfig: allFetchConfig,
      token,
      appliedCriteriaKey,
    });

    if (allFetchKey === lastAllFetchKeyRef.current) {
      return undefined;
    }

    lastAllFetchKeyRef.current = allFetchKey;

    const timeoutId = setTimeout(() => {
      loadSecMasterPage(token, allFetchConfig).catch(() => {
        lastAllFetchKeyRef.current = "";
      });
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [
    allRowsPerPage,
    allTablePage,
    isFilterActive,
    isInitializing,
    isProcessingWatchlistLocked,
    normalizedBondFilter,
    loadSecMasterPage,
    onError,
    open,
    allSortConfig.direction,
    allSortConfig.key,
    activeWatchlistId,
    isCreateWatchlistDraft,
    appliedCriteriaKey,
    appliedFetchCriteria,
    token,
  ]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (isInitializingRef.current || isProcessingWatchlistLocked) {
      return undefined;
    }

    const watchlistPage = isFilterActive ? 0 : myTablePage;
    const watchlistPageSize = myRowsPerPage;
    const watchlistRequestId = resolveWatchlistRequestId(
      activeWatchlistId,
      isCreateWatchlistDraft,
    );

    const fetchConfig = {
      watchlistItemsPage: watchlistPage,
      watchlistItemsPageSize: watchlistPageSize,
      watchlistItemsSortBy: toApiSortKey(mySortConfig.key),
      watchlistItemsSortDirection: mySortConfig.direction.toUpperCase(),
      watchlistItemsSearchString: normalizedBondFilter,
      watchlistItemsWatchlistId: watchlistRequestId,
      criteria: appliedFetchCriteria,
    };

    const fetchKey = buildMyFetchKey({
      fetchConfig,
      token,
      appliedCriteriaKey,
    });

    if (fetchKey === lastMyFetchKeyRef.current) {
      return undefined;
    }

    lastMyFetchKeyRef.current = fetchKey;

    const timeoutId = setTimeout(() => {
      loadWatchlistItemsPage(token, fetchConfig)
        .then((response) => {
          const nextPageCusips = normalizeCusipList(
            response?.watchlistCusips || [],
          );
          const nextItemsPagination = getWatchlistItemsPagination(response);
          setServerWatchlistCusips(nextPageCusips);

          if (isDynamicWatchlist || shouldIgnorePartOfWatchlist) {
            return;
          }

          if (!isFilterActive) {
            const nextPersistedWatchlistTotalCount = Number(
              nextItemsPagination?.totalElements,
            );

            setPersistedWatchlistTotalCount((currentCount) =>
              Number.isFinite(nextPersistedWatchlistTotalCount)
                ? nextPersistedWatchlistTotalCount
                : currentCount,
            );
          }

          setPersistedWatchlistCusips((currentPersisted) => {
            const persistedSet = new Set(currentPersisted);
            const newCusips = nextPageCusips.filter(
              (c) => !persistedSet.has(c),
            );

            if (newCusips.length > 0) {
              setLocalWatchlistCusips((currentLocal) =>
                normalizeCusipList([...currentLocal, ...newCusips]),
              );
            }

            return normalizeCusipList([...currentPersisted, ...newCusips]);
          });
        })
        .catch((loadError) => {
          // Reset the fetch key so that a subsequent identical action triggers a fresh attempt
          lastMyFetchKeyRef.current = "";
        });
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [
    isFilterActive,
    isInitializing,
    isProcessingWatchlistLocked,
    normalizedBondFilter,
    loadWatchlistItemsPage,
    myRowsPerPage,
    myTablePage,
    open,
    mySortConfig.direction,
    mySortConfig.key,
    activeWatchlistId,
    appliedCriteriaKey,
    appliedFetchCriteria,
    isDynamicWatchlist,
    isCreateWatchlistDraft,
    shouldIgnorePartOfWatchlist,
    token,
  ]);

  const filteredBonds = useMemo(() => secMasterBonds, [secMasterBonds]);

  const myWatchlistBonds = useMemo(() => {
    const sourceCusips = serverWatchlistCusips;

    const displayCusips = normalizeCusipList(sourceCusips);

    return displayCusips.map(
      (cusip) =>
        secMasterByCusip[cusip] || {
          cusip,
          bondType: "",
          securityDesc: "",
          description: "",
        },
    );
  }, [serverWatchlistCusips, secMasterByCusip]);

  const watchlistCusipSet = useMemo(
    () => new Set(localWatchlistCusips),
    [localWatchlistCusips],
  );

  const persistedWatchlistCusipSet = useMemo(
    () => new Set(persistedWatchlistCusips),
    [persistedWatchlistCusips],
  );

  const pendingRemovedCusipSet = useMemo(
    () =>
      new Set(
        persistedWatchlistCusips.filter(
          (cusip) => !watchlistCusipSet.has(cusip),
        ),
      ),
    [persistedWatchlistCusips, watchlistCusipSet],
  );

  const pendingAddedCusipSet = useMemo(
    () =>
      new Set(
        localWatchlistCusips.filter(
          (cusip) => !persistedWatchlistCusips.includes(cusip),
        ),
      ),
    [localWatchlistCusips, persistedWatchlistCusips],
  );

  const myWatchlistAddedCount = pendingAddedCusipSet.size;
  const myWatchlistRemovedCount = pendingRemovedCusipSet.size;
  // We no longer preload every persisted CUSIP, so the effective saved count
  // has to start from the backend total and then apply local adds/removals.
  const effectiveStaticWatchlistCusipsCount = useMemo(() => {
    if (isDynamicWatchlist) {
      return 0;
    }

    const baselinePersistedCount = Math.max(
      persistedWatchlistTotalCount,
      persistedWatchlistCusips.length,
    );

    return Math.max(
      0,
      baselinePersistedCount + myWatchlistAddedCount - myWatchlistRemovedCount,
    );
  }, [
    isDynamicWatchlist,
    myWatchlistAddedCount,
    myWatchlistRemovedCount,
    persistedWatchlistCusips.length,
    persistedWatchlistTotalCount,
  ]);
  const hasWatchlistNameChange =
    `${watchlistName || ""}`.trim() !==
    `${currentWatchlist?.watchlistName || ""}`.trim();
  const hasWatchlistTypeChange =
    normalizeWatchlistType(currentWatchlist?.watchlistType) !== watchlistType;
  const hasDynamicCriteriaChange =
    isDynamicWatchlist &&
    initialAppliedCriteriaKeyRef.current !== appliedCriteriaKey;

  const hasWatchlistChanges =
    myWatchlistAddedCount > 0 ||
    myWatchlistRemovedCount > 0 ||
    hasWatchlistNameChange ||
    hasWatchlistTypeChange ||
    hasDynamicCriteriaChange;
  // || (hasUnappliedCriteriaChanges
  //   && Boolean(userCriteria?.hasCriteria || appliedCriteria?.hasCriteria));
  const hasMyWatchlistRows = persistedWatchlistCusips.length > 0;

  const isPersistedCusip = (bond) => {
    const normalizedBondCusip = normalizeCusip(bond?.cusip);
    if (!normalizedBondCusip) {
      return false;
    }

    if (persistedWatchlistCusipSet.has(normalizedBondCusip)) {
      return true;
    }

    if (shouldIgnorePartOfWatchlist) {
      return false;
    }

    return Number(bond?.partOfWatchlist) === 1;
  };

  const getMyWatchlistActionLabel = (bond) => {
    const normalizedBondCusip = normalizeCusip(bond?.cusip);

    if (pendingRemovedCusipSet.has(normalizedBondCusip)) {
      return "Undo";
    }

    return "Delete";
  };

  const ensurePersistedCusipTracked = (bond, normalizedCusip) => {
    // When a bond carries partOfWatchlist=1 from sec master but isn't yet in
    // persistedWatchlistCusips (because we no longer prefetch all cusips),
    // record it so the pending-removed math can include it on save.
    if (
      Number(bond?.partOfWatchlist) === 1 &&
      !persistedWatchlistCusipSet.has(normalizedCusip) &&
      !shouldIgnorePartOfWatchlist
    ) {
      setPersistedWatchlistCusips((current) =>
        current.includes(normalizedCusip)
          ? current
          : [...current, normalizedCusip],
      );
    }
  };

  const handleAddToWatchlist = (bond) => {
    if (isDynamicWatchlist || isProcessingWatchlistLocked) {
      return;
    }

    const normalizedCusip = normalizeCusip(bond?.cusip);

    if (!normalizedCusip || watchlistCusipSet.has(normalizedCusip)) {
      return;
    }

    ensurePersistedCusipTracked(bond, normalizedCusip);
    setLocalWatchlistCusips((current) =>
      current.includes(normalizedCusip)
        ? current
        : [...current, normalizedCusip],
    );
  };

  const handleRemoveFromWatchlist = (bond) => {
    if (
      isDynamicWatchlist
      || isProcessingWatchlistLocked
    ) {
      return;
    }

    const normalizedCusip = normalizeCusip(bond?.cusip);

    if (!normalizedCusip) {
      return;
    }

    ensurePersistedCusipTracked(bond, normalizedCusip);

    setLocalWatchlistCusips((current) => {
      if (current.includes(normalizedCusip)) {
        return current.filter((cusip) => cusip !== normalizedCusip);
      }

      return [...current, normalizedCusip];
    });
  };

  const handleAllTabCheckboxToggle = (bond, checked) => {
    if (checked) {
      handleAddToWatchlist(bond);
      return;
    }

    handleRemoveFromWatchlist(bond);
  };

  const handleClearAll = () => {
    if (isDynamicWatchlist || isProcessingWatchlistLocked) {
      return;
    }

    setLocalWatchlistCusips((current) =>
      current.filter((cusip) => persistedWatchlistCusips.includes(cusip)),
    );
  };

  const handleDeleteAllRequest = () => {
    if (isDynamicWatchlist || isProcessingWatchlistLocked) {
      return;
    }

    if (activeTab === WATCHLIST_TABS.MY_WATCHLIST) {
      if (
        isReadOnlyBackendDynamicWatchlistView ||
        saving ||
        clearingWatchlist ||
        !currentWatchlist?.watchlistId ||
        !hasMyWatchlistRows
      ) {
        return;
      }

      setIsDeleteAllConfirmOpen(true);
      return;
    }

    handleClearAll();
  };

  const handleDismissDeleteAllConfirmation = () => {
    if (clearingWatchlist) {
      return;
    }

    setIsDeleteAllConfirmOpen(false);
  };

  const handleConfirmDeleteAll = async () => {
    try {
      setClearingWatchlist(true);
      const response = await clearCurrentWatchlist(token);
      if (response?.success !== true) {
        throw new Error(response?.message || "Failed to clear watchlist");
      }
      const pendingAdds = localWatchlistCusips.filter(
        (c) => !persistedWatchlistCusips.includes(c),
      );
      setServerWatchlistCusips([]);
      setPersistedWatchlistCusips([]);
      setPersistedWatchlistTotalCount(0);
      setLocalWatchlistCusips(pendingAdds);
      setMyTablePage(0);
      // Applied filters should deliberately NOT be cleared here.
      hasPersistedWatchlistMutationRef.current = true;
      setIsDeleteAllConfirmOpen(false);
    } catch (deleteAllError) {
      // Store handles error
    } finally {
      setClearingWatchlist(false);
    }
  };

  const handleSortChange = (columnKey) => {
    const setTabSortConfig =
      activeTab === WATCHLIST_TABS.ALL ? setAllSortConfig : setMySortConfig;

    setTabSortConfig((currentSortConfig) => {
      const nextDirection =
        currentSortConfig.key === columnKey &&
        currentSortConfig.direction === "asc"
          ? "desc"
          : "asc";

      return {
        key: columnKey,
        direction: nextDirection,
      };
    });

    if (activeTab === WATCHLIST_TABS.ALL) {
      setAllTablePage(0);
      return;
    }

    setMyTablePage(0);
  };

  const applyCriteria = useCallback(async (
    nextUserFilterData,
    { skipDisabledCheck = false } = {}
  ) => {
    if (
      !token
      || isProcessingWatchlistLocked
      || (!skipDisabledCheck && isApplyCriteriaDisabled)) {
        return;
      }

      const applyRequestId = criteriaApplyRequestRef.current + 1;
      criteriaApplyRequestRef.current = applyRequestId;
      const previousAppliedFilterData = appliedFilterData;
      const nextAppliedCriteria = buildWatchlistCriteriaState(
        nextUserFilterData,
        watchlistType,
      );
      const nextAppliedCriteriaKey =
        getWatchlistCriteriaComparisonKey(nextAppliedCriteria);
      const criteria = getFetchCriteria(nextAppliedCriteria);
      const watchlistRequestId = resolveWatchlistRequestId(
        activeWatchlistId,
        isCreateWatchlistDraft,
      );

      try {
        const allFetchConfig = {
          page: 0,
          size: allRowsPerPage,
          sortBy: toApiSortKey(allSortConfig.key),
          sortDirection: allSortConfig.direction.toUpperCase(),
          searchString: normalizedBondFilter,
          watchlistId: watchlistRequestId,
          criteria,
        };

        const allFetchKey = buildAllFetchKey({
          fetchConfig: allFetchConfig,
          token,
          appliedCriteriaKey: nextAppliedCriteriaKey,
        });

        const myFetchConfig = {
          watchlistItemsPage: 0,
          watchlistItemsPageSize: myRowsPerPage,
          watchlistItemsSortBy: toApiSortKey(mySortConfig.key),
          watchlistItemsSortDirection: mySortConfig.direction.toUpperCase(),
          watchlistItemsSearchString: normalizedBondFilter,
          watchlistItemsWatchlistId: watchlistRequestId,
          criteria,
        };

        const myFetchKey = buildMyFetchKey({
          fetchConfig: myFetchConfig,
          token,
          appliedCriteriaKey: nextAppliedCriteriaKey,
        });

        lastAllFetchKeyRef.current = allFetchKey;
        lastMyFetchKeyRef.current = myFetchKey;
        setAllTablePage(0);
        setMyTablePage(0);
        setHasLoadedAllTabCount(false);
        replaceAppliedFilterData(nextUserFilterData);

        const [, matchingWatchlistState] = await Promise.all([
          loadSecMasterPage(token, allFetchConfig)
            .then((response) => {
              if (criteriaApplyRequestRef.current === applyRequestId) {
                setHasLoadedAllTabCount(true);
              }

              return response;
            })
            .catch(() => {
              lastAllFetchKeyRef.current = "";
              if (criteriaApplyRequestRef.current === applyRequestId) {
                setHasLoadedAllTabCount(false);
              }
            }),
          loadWatchlistItemsPage(token, myFetchConfig).catch(() => {
            lastMyFetchKeyRef.current = "";
          }),
        ]);

        if (criteriaApplyRequestRef.current !== applyRequestId) {
          return;
        }

        const matchingWatchlistCusips = normalizeCusipList(
          matchingWatchlistState?.watchlistCusips || [],
        );

        setServerWatchlistCusips(matchingWatchlistCusips);
      } catch (applyError) {
        if (criteriaApplyRequestRef.current !== applyRequestId) {
          return;
        }

        lastAllFetchKeyRef.current = "";
        lastMyFetchKeyRef.current = "";
        setHasLoadedAllTabCount(false);
        // Store handles error
      }
    },
    [
    activeWatchlistId,
    appliedFilterData,
    allRowsPerPage,
    allSortConfig.direction,
    allSortConfig.key,
    isApplyCriteriaDisabled,
    isCreateWatchlistDraft,
    loadSecMasterPage,
    loadWatchlistItemsPage,
    myRowsPerPage,
    mySortConfig.direction,
    mySortConfig.key,
    normalizedBondFilter,
    onError,
    defaultFilterData,
    replaceAppliedFilterData,
    shouldIgnorePartOfWatchlist,
    isProcessingWatchlistLocked,
    token,
    watchlistType,
  ]);
  const handleApplyCriteria = useCallback(async () => {
    await applyCriteria(userFilterData);
  }, [applyCriteria, userFilterData]);

  const handleClearFilters = useCallback(() => {
    if (isClearFiltersDisabled) {
      return;
    }

    const resetFilterShape = Object.keys(appliedFilterData || {}).reduce(
      (nextData, key) => ({
        ...nextData,
        [key]: "",
      }),
      {},
    );

    const nextFilterData = {
      ...resetFilterShape,
      ...defaultFilterData,
    };

    replaceUserFilterData(nextFilterData);

    const nextAppliedCriteria = buildWatchlistCriteriaState(
      nextFilterData,
      watchlistType,
    );
    const nextAppliedCriteriaKey =
      getWatchlistCriteriaComparisonKey(nextAppliedCriteria);

    if (nextAppliedCriteriaKey === appliedCriteriaKey) {
      return;
    }

    void applyCriteria(nextFilterData, { skipDisabledCheck: true });
  }, [
    appliedCriteriaKey,
    appliedFilterData,
    applyCriteria,
    defaultFilterData,
    isClearFiltersDisabled,
    replaceUserFilterData,
    watchlistType,
  ]);

  const handleClearFilterFieldsAndApply = useCallback(
    (fieldsToClear = []) => {
      const normalizedFields = Array.from(
        new Set(
          (Array.isArray(fieldsToClear)
            ? fieldsToClear
            : [fieldsToClear]
          ).filter(Boolean),
        ),
      );

      if (normalizedFields.length === 0) {
        return;
      }

      const nextFilterData = {
        ...appliedFilterData,
      };

      normalizedFields.forEach((field) => {
        nextFilterData[field] = "";
      });

      replaceUserFilterData(nextFilterData);

      const nextAppliedCriteria = buildWatchlistCriteriaState(
        nextFilterData,
        watchlistType,
      );
      const nextAppliedCriteriaKey =
        getWatchlistCriteriaComparisonKey(nextAppliedCriteria);

      if (nextAppliedCriteriaKey === appliedCriteriaKey) {
        return;
      }

      void applyCriteria(nextFilterData, { skipDisabledCheck: true });
    },
    [
      appliedCriteriaKey,
      appliedFilterData,
      applyCriteria,
      replaceUserFilterData,
      watchlistType,
    ],
  );

  const handleCancel = () => {
    if (saving || clearingWatchlist) {
      return;
    }

    if (!hasWatchlistChanges) {
      const watchlistChanged = hasPersistedWatchlistMutationRef.current;
      clearPopupState();
      onClose?.({
        saved: false,
        watchlistChanged,
        watchlistId: currentWatchlist?.watchlistId ?? null,
      });
      return;
    }

    setIsCloseConfirmOpen(true);
  };

  const handleDismissCloseConfirmation = () => {
    setIsCloseConfirmOpen(false);
  };

  const handleConfirmClose = () => {
    const watchlistChanged = hasPersistedWatchlistMutationRef.current;
    clearPopupState();
    onClose?.({
      saved: false,
      watchlistChanged,
      watchlistId: currentWatchlist?.watchlistId ?? null,
    });
  };

  const handleModalCloseRequest = (_event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancel();
  };

  const executeSave = async (dynamicCriteriaOverride = null) => {
    const isUpdate =
      Boolean(currentWatchlist?.watchlistId) &&
      currentWatchlist.watchlistId !== CREATE_WATCHLIST_ID;

    try {
      const response = await saveWatchlist(token, {
        watchlistName,
        nextCusips: localWatchlistCusips,
        baseCusips: persistedWatchlistCusips,
        watchlistType,
        switchedFromBackendDynamicToStatic: hasSwitchedBackendDynamicToStatic,
        dynamicCriteria:
          watchlistType === WATCHLIST_TYPE.DYNAMIC
            ? dynamicCriteriaOverride
            : null,
      });
      const responseStatus = response?.status ?? WATCHLIST_STATUS.DONE;
      if (response?.noop) {
        onSuccess?.("No watchlist changes to save");
      } else {
        hasPersistedWatchlistMutationRef.current = true;
      }

      const watchlistChanged =
        hasPersistedWatchlistMutationRef.current
        || (!response?.noop && responseStatus === WATCHLIST_STATUS.PROCESSING);
      const closingWatchlistId =
        response?.watchlist?.watchlistId ??
        currentWatchlist?.watchlistId ??
        null;
      clearPopupState();
      onClose?.({
        saved: !response?.noop && responseStatus === WATCHLIST_STATUS.DONE,
        watchlistChanged,
        watchlistId: closingWatchlistId,
        status: responseStatus,
      });
    } catch (saveError) {
      // Store handles saving error
    }
  };

  const handleRetryFailedWatchlist = useCallback(async () => {
    if (
      saving ||
      clearingWatchlist ||
      !canRetryFailedBackendDynamicWatchlist
    ) {
      return;
    }

    try {
      const response = await retryFailedDynamicWatchlist(token);
      const responseStatus = response?.status ?? WATCHLIST_STATUS.DONE;
      const closingWatchlistId =
        response?.watchlist?.watchlistId ??
        currentWatchlist?.watchlistId ??
        null;

      hasPersistedWatchlistMutationRef.current = true;
      clearPopupState();
      onClose?.({
        saved: responseStatus === WATCHLIST_STATUS.DONE,
        watchlistChanged: true,
        watchlistId: closingWatchlistId,
        status: responseStatus,
      });
    } catch (retryError) {
      // Store handles retry error
    }
  }, [
    canRetryFailedBackendDynamicWatchlist,
    clearPopupState,
    clearingWatchlist,
    currentWatchlist?.watchlistId,
    onClose,
    retryFailedDynamicWatchlist,
    saving,
    token,
  ]);

  const handleSave = async () => {
    if (isProcessingWatchlistLocked) {
      return;
    }

    if (!`${watchlistName || ""}`.trim()) {
      setError("Watchlist name is required");
      return;
    }

    if (
      !isDynamicWatchlist &&
      effectiveStaticWatchlistCusipsCount > MA_STATIC_WATCHLIST_CUSIP_LIMIT
    ) {
      setError(MA_STATIC_WATCHLIST_LIMIT_MESSAGE);
      return;
    }

    if (isDynamicWatchlist && !userCriteria?.hasCriteria) {
      setIsDynamicNoCriteriaErrorOpen(true);
      return;
    }

    if (isDynamicWatchlist && hasUnappliedCriteriaChanges) {
      setIsDynamicSaveCriteriaWarningOpen(true);
      return;
    }

    if (isExceedingDynamicLimit) {
      setError(MA_DYNAMIC_WATCHLIST_LIMIT_MESSAGE);
      return;
    }

    await executeSave(isDynamicWatchlist ? appliedCriteria : null);
  };

  const handleDismissDynamicSaveCriteriaWarning = () => {
    if (saving) {
      return;
    }

    setIsDynamicSaveCriteriaWarningOpen(false);
  };

  const handleApplyAndReviewDynamicCriteria = async () => {
    if (saving) {
      return;
    }

    setIsDynamicSaveCriteriaWarningOpen(false);
    await handleApplyCriteria();
  };

  const handleContinueDynamicSaveWithAppliedCriteria = async () => {
    if (saving || !hasAppliedDynamicCriteria) {
      return;
    }

    setIsDynamicSaveCriteriaWarningOpen(false);
    await executeSave(appliedCriteria);
  };

  const handleDismissDynamicNoCriteriaError = () => {
    if (saving) {
      return;
    }

    setIsDynamicNoCriteriaErrorOpen(false);
  };

  const visibleBonds =
    activeTab === WATCHLIST_TABS.ALL ? filteredBonds : myWatchlistBonds;

  const totalVisibleBonds =
    activeTab === WATCHLIST_TABS.ALL
      ? secMasterPagination.totalElements
      : itemsPagination.totalElements;

  const paginatedVisibleBonds = useMemo(() => visibleBonds, [visibleBonds]);

  useEffect(() => {
    if (activeTab !== WATCHLIST_TABS.MY_WATCHLIST) {
      return;
    }

    const maxPage = Math.max(
      0,
      Math.ceil(totalVisibleBonds / myRowsPerPage) - 1,
    );
    if (myTablePage > maxPage) {
      setMyTablePage(maxPage);
    }
  }, [activeTab, myRowsPerPage, myTablePage, totalVisibleBonds]);

  const isAllTabLoading = secMasterLoading || isInitializingRef.current;
  const isMyTabLoading = loading || isInitializingRef.current;
  const hasAppliedFilterOrCharacteristics = Boolean(
    appliedCriteria?.hasCriteria,
  );

  const emptyMessage =
    activeTab === WATCHLIST_TABS.ALL
      ? isAllTabLoading
        ? "Loading security data..."
        : hasAppliedFilterOrCharacteristics
          ? "No data matches your applied filter/characteristics."
          : isFilterActive
            ? "No bonds match the current bond filter."
            : "No security data is available at the moment. Please try again later."
      : isMyTabLoading
        ? "Loading watchlist..."
        : hasAppliedFilterOrCharacteristics
          ? "No data matches your applied filter/characteristics."
          : isFilterActive
            ? "No bonds match the current bond filter."
            : isReadOnlyBackendDynamicWatchlistView
              ? "No data is available for this watchlist."
              : isDynamicWatchlist
                ? "No data is available for this dynamic watchlist."
                : "Your watchlist is empty. Add securities from the All tab to get started.";

  const pendingAddedCount = pendingAddedCusipSet.size;

  const isAllTabBondChecked = (bond) => {
    if (isDynamicWatchlist) {
      return Boolean(appliedCriteria?.hasCriteria);
    }

    const normalizedCusip = normalizeCusip(bond?.cusip);

    if (watchlistCusipSet.has(normalizedCusip)) {
      return true;
    }

    if (pendingRemovedCusipSet.has(normalizedCusip)) {
      return false;
    }

    return isPersistedCusip(bond);
  };

  const isMyTabBondDiscarded = (bond) =>
    !isDynamicWatchlist &&
    pendingRemovedCusipSet.has(normalizeCusip(bond?.cusip));

  const applyBondFilter = (nextFilterValue) => {
    setBondFilter(sanitizeBondFilterValue(nextFilterValue));
    setAllTablePage(0);
    setMyTablePage(0);
  };

  const handleBondFilterChange = (event) => {
    applyBondFilter(event.target.value);
  };

  const handleBondFilterPaste = (event) => {
    event.preventDefault();
    const pastedValue = event.clipboardData?.getData("text") || "";
    applyBondFilter(pastedValue);
  };

  const handleTablePageChange = (_event, newPage) => {
    if (activeTab === WATCHLIST_TABS.ALL) {
      setAllTablePage(newPage);
      return;
    }

    setMyTablePage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);

    if (activeTab === WATCHLIST_TABS.ALL) {
      setAllRowsPerPage(nextRowsPerPage);
      setAllTablePage(0);
      return;
    }

    setMyRowsPerPage(nextRowsPerPage);
    setMyTablePage(0);
  };

  return {
    modalHeight,
    watchlists: selectableWatchlists,
    activeWatchlistId,
    watchlistName,
    setWatchlistName,
    hasWatchlists: selectableWatchlists.length > 0,
    handleWatchlistSelect,
    handleCreateWatchlistDraft,
    isCreateWatchlistDraft,
    isInitializing: isInitializingRef.current,
    isFirstFetchStarted,
    currentWatchlist,
    loading,
    secMasterLoading,
    saving,
    error,
    secMasterError,
    watchlistItemsError,
    clearError,
    clearSecMasterError,
    clearWatchlistItemsError,
    secMasterPagination,
    itemsPagination,
    activeTab,
    setActiveTab,
    watchlistType,
    setWatchlistType: handleWatchlistTypeChange,
    isDynamicWatchlist,
    isReadOnlyBackendDynamicWatchlistView,
    isProcessingWatchlistLocked,
    isFailedWatchlist,
    canRetryFailedDynamicWatchlist: canRetryFailedBackendDynamicWatchlist,
    processingWatchlistMessage,
    failedWatchlistMessage,
    bondFilter,
    handleBondFilterChange,
    handleBondFilterPaste,
    allSortConfig,
    mySortConfig,
    paginatedVisibleBonds,
    totalVisibleBonds,
    hasLoadedAllTabCount,
    hasLoadedMyTabCount,
    emptyMessage,
    myWatchlistAddedCount: isDynamicWatchlist ? 0 : myWatchlistAddedCount,
    myWatchlistRemovedCount: isDynamicWatchlist ? 0 : myWatchlistRemovedCount,
    hasMyWatchlistRows,
    pendingAddedCount,
    isDeleteAllConfirmOpen,
    isCloseConfirmOpen,
    isDynamicToStaticConfirmOpen,
    isDynamicSaveCriteriaWarningOpen,
    isDynamicNoCriteriaErrorOpen,
    hasAppliedDynamicCriteria,
    isExceedingDynamicLimit,
    clearingWatchlist,
    isApplyCriteriaDisabled,
    isClearFiltersDisabled,
    rowsPerPageOptions,
    allTablePage,
    myTablePage,
    allRowsPerPage,
    myRowsPerPage,
    handleTablePageChange,
    handleRowsPerPageChange,
    handleCancel,
    handleSave,
    handleRetryFailedWatchlist,
    handleModalCloseRequest,
    handleDeleteAllRequest,
    handleDismissDeleteAllConfirmation,
    handleConfirmDeleteAll,
    handleDismissCloseConfirmation,
    handleConfirmClose,
    handleDismissDynamicToStaticConfirmation,
    handleConfirmDynamicToStaticConfirmation,
    handleDismissDynamicSaveCriteriaWarning,
    handleApplyAndReviewDynamicCriteria,
    handleContinueDynamicSaveWithAppliedCriteria,
    handleDismissDynamicNoCriteriaError,
    handleSortChange,
    handleApplyCriteria,
    isAllTabBondChecked,
    isPersistedCusip,
    handleAllTabCheckboxToggle,
    handleRemoveFromWatchlist,
    isMyTabBondDiscarded,
    getMyWatchlistActionLabel,
    forceExpandFilter,
    resetForceExpandFilter,
    filterData: userFilterData,
    appliedFilters: appliedCriteria?.filters || null,
    filterOptions,
    loadingFilterDefaults,
    filterDefaultsError,
    clearFilterDefaultsError,
    setFilterField: handleFilterFieldChange,
    resetFilterData: handleClearFilters,
    clearFilterFieldsAndApply: handleClearFilterFieldsAndApply,
    resetFilterDataKeepBondType: resetUserFilterDataKeepBondType,
    effectiveStaticWatchlistCusipsCount,
  };
};
